import * as THREE from 'three';

import type { SpaceKey } from '@toolproof-core/visualization/_contract/key';
import type { SpaceDerivationFrame, TransitionPhase } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toBooleanOperationJunction } from '@/adapters/primitive/_pure/derivation/boolean/geometry';
import { toBooleanWheelPositions } from '@/adapters/primitive/_pure/derivation/boolean/traceProjection';
import { toBooleanRodAnchorItemKey, toBooleanRodItemKey } from '@/adapters/primitive/_pure/derivation/keyCodec';
import {
    buildPrimitiveRodFlightPatch,
    buildPrimitiveRodFrame,
    toPrimitiveRodPlacementBasis,
    toScopedPrimitiveRodKey,
    type PrimitiveRodPlacement,
} from '@/adapters/primitive/_pure/derivation/primitiveRod';
import { toPrimitiveGeometryFramePatch } from '@/adapters/primitive/_pure/derivation/primitiveStepTransitions';
import { BOOLEAN_OPERATIONS } from '@/adapters/primitive/_data/booleanOperations';
import { buildResourceTypeRadialConnectorItemKey } from '@/workflow/_pure/keyCodec';
import type { BooleanAlgebraOperation } from '@/adapters/primitive/_contract/boolean/operation';
import type { BooleanRodState } from '@/adapters/primitive/_contract/boolean/rod';
import type { Config as PrimitiveConfig, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';
import type { BooleanGraphTransformationConfig } from '@/workflow-runner/_contract/config';

export function deriveBooleanGraphConfig(args: {
    config: PrimitiveConfig;
    operation: BooleanAlgebraOperation;
    workflowPlacement: PrimitiveRodPlacement;
    ringIntersection: readonly [number, number, number];
}): PrimitiveConfig {
    const result = args.workflowPlacement.positionByRole['boolean-output'];
    if (result === undefined) throw new Error('Boolean graph requires a workflow Result position.');
    const { axis } = toPrimitiveRodPlacementBasis(args.workflowPlacement);
    const resultToIntersection = new THREE.Vector3(...args.ringIntersection).sub(new THREE.Vector3(...result));
    const distance = resultToIntersection.dot(axis);
    if (!Number.isFinite(distance) || distance <= 1e-12
        || resultToIntersection.clone().addScaledVector(axis, -distance).length() > 1e-8) {
        throw new Error('Boolean graph requires the ToolStep-intersection ahead of Result on its alignment.');
    }
    const positions = toBooleanWheelPositions(args.config, args.operation);
    const junction = toBooleanOperationJunction({
        inputOne: positions.inputOne,
        ...(args.operation === 'logical-not' ? {} : { inputTwo: positions.inputTwo }),
        output: positions.output,
        wheelGap: args.config.boolean.display.track.wheelEndGap,
        height: BOOLEAN_OPERATIONS[args.operation].paths.length === 1 ? 0 : args.config.boolean.display.layout.branchHeight,
    });
    // Result geometry is fitted first; native wheel dimensions constrain the operand angle.
    const scale = distance / (junction[2] - positions.output[2]);
    const track = args.config.boolean.display.track;
    const fitted: PrimitiveConfig = {
        ...args.config,
        base: {
            ...args.config.base,
            display: {
                ...args.config.base.display,
                wheel: { ...args.config.base.display.wheel, wheelGap: args.config.base.display.wheel.wheelGap * scale },
            },
        },
        boolean: {
            ...args.config.boolean,
            display: {
                ...args.config.boolean.display,
                layout: {
                    operandSpreadAngleDegrees: args.config.boolean.display.layout.operandSpreadAngleDegrees,
                    resultChannelDepth: args.config.boolean.display.layout.resultChannelDepth * scale,
                    branchHeight: args.config.boolean.display.layout.branchHeight * scale,
                },
                track: {
                    ...track,
                    halfGap: track.halfGap * scale,
                    lipThickness: track.lipThickness * scale,
                    lipHeight: track.lipHeight * scale,
                    floorDepth: track.floorDepth * scale,
                    barHeight: track.barHeight * scale,
                    wheelEndGap: track.wheelEndGap * scale,
                },
                splitMarker: { ...args.config.boolean.display.splitMarker, halfWidth: args.config.boolean.display.splitMarker.halfWidth * scale },
            },
        },
    };
    toBooleanWheelPositions(fitted, args.operation);
    return fitted;
}

export function deriveBooleanGraphPlacement(
    workflowPlacement: PrimitiveRodPlacement,
    config: PrimitiveConfig,
): PrimitiveRodPlacement {
    const result = workflowPlacement.positionByRole['boolean-output'];
    if (result === undefined) throw new Error('Boolean graph requires a workflow Result position.');
    const { axis } = toPrimitiveRodPlacementBasis(workflowPlacement);
    // Primitive Result is at -wheelGap. Translating its origin keeps it stationary.
    const origin = new THREE.Vector3(...result).addScaledVector(axis, config.base.display.wheel.wheelGap);
    return { ...workflowPlacement, origin: [origin.x, origin.y, origin.z] };
}

type GraphFrameArgs = {
    spaceKey: SpaceKey;
    config: PrimitiveConfig;
    wheels: readonly PrimitiveWheelState[];
    booleanRod: BooleanRodState;
    workflowPlacement: PrimitiveRodPlacement;
    graphPlacement: PrimitiveRodPlacement;
    scope: string;
    layoutProgress?: number;
    profileProgress?: number;
    simpleRods?: boolean;
    retireEmphasis?: boolean;
};

export function buildBooleanGraphFrame(args: GraphFrameArgs): SpaceDerivationFrame {
    const layoutProgress = args.layoutProgress ?? 1;
    const profileProgress = args.profileProgress ?? 1;
    const { axis, tangent, up } = toPrimitiveRodPlacementBasis(args.graphPlacement);
    const graphOrigin = args.graphPlacement.origin;
    if (graphOrigin === undefined) throw new Error('Boolean graph requires a rigid placement origin.');
    const origin = new THREE.Vector3(...graphOrigin);
    const local = (position: readonly [number, number, number]): [number, number, number] => {
        const offset = new THREE.Vector3(...position).sub(origin);
        return [offset.dot(tangent), offset.dot(up), offset.dot(axis)];
    };
    const sourceByRole = Object.fromEntries(Object.entries(args.workflowPlacement.positionByRole)
        .flatMap(([role, position]) => position === undefined ? [] : [[role, local(position)]]));
    const inputOne = sourceByRole['boolean-input-one'];
    const inputTwo = sourceByRole['boolean-input-two'];
    const result = sourceByRole['boolean-output'];
    if (inputOne === undefined || result === undefined) throw new Error('Boolean graph requires Operand and Result anchors.');
    const inputDepth = (inputOne[2] + (inputTwo?.[2] ?? inputOne[2])) / 2;
    const gap = args.config.base.display.wheel.wheelGap;
    const collapsed = (position: readonly [number, number, number]): [number, number, number] => [
        0, 0, result[2] + (position[2] + gap) / (2 * gap) * (inputDepth - result[2]),
    ];
    const lerp = (from: readonly number[], to: readonly number[]): [number, number, number] => [
        from[0] + (to[0] - from[0]) * layoutProgress,
        from[1] + (to[1] - from[1]) * layoutProgress,
        from[2] + (to[2] - from[2]) * layoutProgress,
    ];
    const wheels = args.wheels.map((wheel) => ({
        ...wheel,
        position: wheel.role === 'boolean-output' ? wheel.position
            : lerp(sourceByRole[wheel.role] ?? collapsed(wheel.position), wheel.position),
    }));
    const segments = args.booleanRod.segments
        .filter((segment) => !args.simpleRods || (!segment.segmentKey.startsWith('traveller:')
            && (segment.profile !== undefined || segment.opacity > 0)))
        .map((segment) => {
        const from = segment.segmentKey.endsWith(':input-one') ? inputOne
            : segment.segmentKey.endsWith(':input-two') ? inputTwo ?? inputOne
                : collapsed(segment.from);
        const thin = args.config.boolean.display.track.lipThickness * 0.02;
        const endpoints = {
            from: lerp(from, segment.from),
            to: lerp(collapsed(segment.to), segment.to),
        };
        if (args.simpleRods) {
            return {
                segmentKey: segment.segmentKey, ...endpoints,
                color: args.config.base.display.placeholder.outlineColor, opacity: 1,
            };
        }
        return {
            ...segment,
            ...endpoints,
            ...(segment.profile === undefined ? {} : {
                profile: {
                    ...segment.profile,
                    ...(args.retireEmphasis && segment.profile.border ? {
                        border: { ...segment.profile.border, opacity: 0 },
                    } : {}),
                    innerWidth: thin + (segment.profile.innerWidth - thin) * profileProgress,
                    lipThickness: thin + (segment.profile.lipThickness - thin) * profileProgress,
                    lipHeight: thin + (segment.profile.lipHeight - thin) * profileProgress,
                    floorDepth: segment.profile.floorDepth === 0 ? 0
                        : thin + (segment.profile.floorDepth - thin) * profileProgress,
                },
            }),
        };
    });
    const radialAnchors = Object.values(args.workflowPlacement.positionByRole)
        .flatMap((position) => position === undefined ? [] : [position])
        .sort((left, right) => Math.hypot(left[0], left[2]) - Math.hypot(right[0], right[2]));
    segments.push({
        segmentKey: 'workflow-alignment-boundary',
        from: local(radialAnchors[0]), to: local(radialAnchors[radialAnchors.length - 1]),
        color: args.config.boolean.display.track.floorColor,
        opacity: 0,
    });
    if (inputTwo !== undefined) {
        for (const role of ['boolean-input-one', 'boolean-input-two'] as const) {
            const wheel = wheels.find((wheel) => wheel.role === role);
            if (wheel === undefined) throw new Error(`Boolean continuity fork requires ${role}.`);
            segments.push({
                segmentKey: `workflow-continuity:${role}`,
                from: inputOne, to: wheel.position,
                color: args.config.base.display.placeholder.outlineColor,
                opacity: 1,
            });
        }
    }
    return buildPrimitiveRodFrame({
        spaceKey: args.spaceKey, config: args.config, wheels,
        booleanRod: { segments }, scope: args.scope, placement: args.graphPlacement,
    });
}

/** Cut only the active interval out of the shared Boolean radial; keep both surrounding portions. */
export function replaceBooleanAlignment(frame: SpaceDerivationFrame, scope: string): SpaceDerivationFrame {
    const radialConnectorKey = buildResourceTypeRadialConnectorItemKey({
        resourceTypeItemKey: 'item:entity:TYPE-Boolean',
        toolStepKey: 'boolean-operation',
        direction: 'input',
    });
    const itemCollections = frame.itemCollections.map((collection) => {
        if (collection.itemCollectionKind !== 'connector') return collection;
        return {
            ...collection,
            items: collection.items.flatMap((item) => item.itemKey !== radialConnectorKey ? [item] : [
                {
                    ...item,
                    itemKey: `${item.itemKey}:boolean-before` as typeof item.itemKey,
                    endpoints: { from: item.endpoints.from, to: {
                        endpointKind: 'static' as const,
                        itemKey: toScopedPrimitiveRodKey(toBooleanRodAnchorItemKey('workflow-alignment-boundary', 'from'), scope),
                        anchorKey: 'center' as const,
                    } },
                },
                {
                    ...item,
                    itemKey: `${item.itemKey}:boolean-after` as typeof item.itemKey,
                    endpoints: { from: {
                        endpointKind: 'static' as const,
                        itemKey: toScopedPrimitiveRodKey(toBooleanRodAnchorItemKey('workflow-alignment-boundary', 'to'), scope),
                        anchorKey: 'center' as const,
                    }, to: item.endpoints.to },
                },
            ]),
        };
    });
    const state = frame.itemParametricState?.connectorItemByKey?.[radialConnectorKey];
    const continuityStates = Object.fromEntries(['boolean-input-one', 'boolean-input-two'].flatMap((role) => {
        const key = toScopedPrimitiveRodKey(toBooleanRodItemKey(`workflow-continuity:${role}`), scope);
        const current = frame.itemParametricState?.connectorItemByKey?.[key];
        return current === undefined ? [] : [[key, { ...current, ...state }]];
    }));
    return {
        ...frame, itemCollections,
        itemParametricState: {
            ...frame.itemParametricState,
            connectorItemByKey: {
                ...frame.itemParametricState?.connectorItemByKey,
                [`${radialConnectorKey}:boolean-before`]: state,
                [`${radialConnectorKey}:boolean-after`]: state,
                ...continuityStates,
            },
        },
    };
}

export function restoreBooleanAlignment(frame: SpaceDerivationFrame): SpaceDerivationFrame {
    const radialConnectorKey = buildResourceTypeRadialConnectorItemKey({
        resourceTypeItemKey: 'item:entity:TYPE-Boolean',
        toolStepKey: 'boolean-operation',
        direction: 'input',
    });
    return {
        ...frame,
        itemCollections: frame.itemCollections.map((collection) => {
            if (collection.itemCollectionKind !== 'connector') return collection;
            const before = collection.items.find((item) => item.itemKey === `${radialConnectorKey}:boolean-before`);
            const after = collection.items.find((item) => item.itemKey === `${radialConnectorKey}:boolean-after`);
            if (before === undefined || after === undefined) return collection;
            return {
                ...collection,
                items: [
                    ...collection.items.filter((item) => item !== before && item !== after),
                    { ...before, itemKey: radialConnectorKey, endpoints: { from: before.endpoints.from, to: after.endpoints.to } },
                ],
            };
        }),
    };
}

export function deriveBooleanGraphTransitionPhases(args: GraphFrameArgs & {
    exit: boolean;
    timing: BooleanGraphTransformationConfig;
    wrapFrame: (frame: SpaceDerivationFrame) => SpaceDerivationFrame;
}): TransitionPhase[] {
    for (const [key, value] of Object.entries(args.timing)) {
        if (!Number.isFinite(value) || value < 0) {
            throw new RangeError(`Boolean graph transformation timing '${key}' must be finite and non-negative.`);
        }
    }
    const durations = args.exit
        ? [args.timing.refoldProfileClosingDurationMs, args.timing.refoldWheelMovementDurationMs]
        : [args.timing.unfoldWheelMovementDurationMs, args.timing.unfoldProfileOpeningDurationMs];
    const states = args.exit
        ? [
            { layoutProgress: 1, profileProgress: 1 },
            { layoutProgress: 1, profileProgress: 0, retireEmphasis: true },
            { layoutProgress: 1, profileProgress: 0, simpleRods: true },
            { layoutProgress: 0, profileProgress: 0, simpleRods: true },
        ]
        : [
            { layoutProgress: 0, profileProgress: 0, simpleRods: true },
            { layoutProgress: 1, profileProgress: 0, simpleRods: true },
            { layoutProgress: 1, profileProgress: 0 },
            { layoutProgress: 1, profileProgress: 1 },
        ];
    const frames = states.map((state) => buildBooleanGraphFrame({
        ...args, ...state,
    }));
    const prefix = `boolean-graph:${args.scope}:${args.exit ? 'exit' : 'entry'}`;
    const phases: TransitionPhase[] = [{
        patch: toPrimitiveGeometryFramePatch(args.wrapFrame(frames[0])),
        transitionOptions: { transitionKind: 'instant', transitionKey: `${prefix}:prepare` },
    }];
    for (let index = 1; index < frames.length; index++) {
        if (index === 2) {
            phases.push({
                patch: toPrimitiveGeometryFramePatch(args.wrapFrame(frames[index])),
                transitionOptions: { transitionKind: 'instant', transitionKey: `${prefix}:representation` },
            });
            continue;
        }
        const patch = buildPrimitiveRodFlightPatch({
            spaceKey: args.spaceKey, config: args.config,
            previousWheels: [], nextWheels: [],
            previousFrame: frames[index - 1], nextFrame: frames[index],
        });
        if (patch === null) throw new Error('Boolean graph morph produced no geometry patch.');
        phases.push({
            patch,
            transitionOptions: {
                transitionKind: 'interpolated', transitionKey: `${prefix}:${index}`,
                duration: durations[index === 1 ? 0 : 1],
            },
        });
    }
    return phases;
}
